import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, ChevronDown } from "lucide-react";
import { navData } from "../../data/mockData";
import LanguageSelector from "./LanguageSelector";
import { scrollToSection } from "../../lib/scrollToSection";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "../ui/sheet";

const PHONES = [
  { display: navData.phone, link: navData.phoneLink, label: "Main" },
];

const NAV = [
  { key: "home", label: "Home" },
  { key: "services", label: "Services", section: "services" },
  { key: "commercial", label: "Commercial", to: "/commercial-cleaning-cardiff", wide: true },
  { key: "carpet", label: "Carpet Cleaning", to: "/carpet-cleaning-cardiff" },
  { key: "landlords", label: "Landlords", to: "/landlord-letting-agent-cleaning-cardiff" },
  { key: "reviews", label: "Reviews", section: "reviews" },
];

const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cleanora-primary focus-visible:ring-offset-2";

const useNavActions = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const go = (item) => {
    if (item.to) {
      navigate(item.to);
      window.scrollTo({ top: 0 });
      return;
    }
    if (pathname !== "/") navigate("/");
    scrollToSection(item.section);
  };
  const isActive = (item) =>
    item.key === "home"
      ? pathname === "/"
      : item.to
        ? pathname === item.to
        : item.key === "services" && pathname.endsWith("-cardiff") && !NAV.some((n) => n.to === pathname);
  return { go, isActive };
};

const Logo = () => {
  const { go } = useNavActions();
  return (
    <a
      href="/"
      onClick={(e) => { e.preventDefault(); go(NAV[0]); }}
      className={`flex-shrink-0 rounded-lg ${focusRing}`}
      aria-label={`${navData.brandName} ${navData.tagline} – home`}
      data-testid="header-logo-link"
    >
      <img src={navData.logo} alt={`${navData.brandName} ${navData.tagline}`} className="h-11 sm:h-14 w-auto max-w-none object-contain" data-testid="header-logo-img" />
    </a>
  );
};

const DesktopNav = () => {
  const { go, isActive } = useNavActions();
  return (
    <nav aria-label="Main" className="hidden lg:flex items-center gap-1">
      {NAV.map((item) => (
        <button
          key={item.key}
          type="button"
          onClick={() => go(item)}
          aria-current={isActive(item) ? "page" : undefined}
          className={`relative px-3 py-2 rounded-full text-[15px] font-semibold transition-colors ${item.wide ? "hidden xl:inline-block" : ""} ${focusRing} ${
            isActive(item) ? "text-cleanora-primary bg-cleanora-soft-mist" : "text-slate-700 hover:text-cleanora-primary hover:bg-slate-50"
          }`}
          data-testid={`nav-${item.key}-link`}
        >
          {item.label}
        </button>
      ))}
    </nav>
  );
};

const ContactMenu = () => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button type="button" className={`hidden lg:inline-flex items-center gap-2 h-11 px-4 rounded-full text-sm font-semibold text-slate-700 hover:text-cleanora-primary hover:bg-slate-50 transition-colors ${focusRing}`} data-testid="header-contact-trigger">
        <Phone className="w-4 h-4" /> Contact <ChevronDown className="w-4 h-4 text-slate-400" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-60 rounded-xl p-2" data-testid="header-contact-menu">
      <DropdownMenuLabel className="text-xs uppercase tracking-wide text-slate-500">Call us</DropdownMenuLabel>
      {PHONES.map((p, i) => (
        <DropdownMenuItem key={p.link} asChild className="cursor-pointer py-2.5">
          <a href={`tel:${p.link}`} className="flex items-center gap-3 font-semibold text-slate-800" data-testid={`header-phone-${i + 1}`}>
            <Phone className="w-4 h-4 text-cleanora-primary" /> {p.display} <span className="ml-auto text-xs font-medium text-slate-400">{p.label}</span>
          </a>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const { go, isActive } = useNavActions();
  const select = (item) => { setOpen(false); go(item); };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" aria-label="Open menu" className={`lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 ${focusRing}`} data-testid="mobile-menu-trigger">
          <Menu className="w-5 h-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[min(20rem,85vw)] flex flex-col gap-6" data-testid="mobile-menu-panel">
        <SheetTitle className="text-left">Menu</SheetTitle>
        <SheetDescription className="sr-only">Site navigation and contact numbers</SheetDescription>
        <nav aria-label="Mobile" className="flex flex-col gap-1">
          {NAV.map((item) => (
            <button key={item.key} type="button" onClick={() => select(item)} aria-current={isActive(item) ? "page" : undefined}
              className={`text-left px-4 py-3 rounded-xl text-base font-semibold ${focusRing} ${isActive(item) ? "bg-cleanora-soft-mist text-cleanora-primary" : "text-slate-800 hover:bg-slate-50"}`}
              data-testid={`mobile-nav-${item.key}-link`}>
              {item.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-slate-100 pt-5">
          <p className="px-4 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Call us</p>
          {PHONES.map((p, i) => (
            <a key={p.link} href={`tel:${p.link}`} className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-slate-800 hover:bg-slate-50 ${focusRing}`} data-testid={`mobile-phone-${i + 1}`}>
              <Phone className="w-4 h-4 text-cleanora-primary" /> {p.display} <span className="ml-auto text-xs font-medium text-slate-400">{p.label}</span>
            </a>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const QuoteButton = ({ onClick }) => (
  <button type="button" onClick={onClick}
    className={`inline-flex items-center justify-center h-11 px-5 sm:px-6 rounded-full bg-gradient-to-r from-cleanora-primary to-cleanora-primary-dark text-white text-sm sm:text-[15px] font-bold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200 motion-reduce:transition-none whitespace-nowrap ${focusRing}`}
    data-testid="header-get-quote-btn">
    Get a Quote
  </button>
);

const Header = ({ scrollToQuote }) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-50 bg-white/95 backdrop-blur-md transition-shadow duration-300 border-b ${isScrolled ? "shadow-md border-slate-100" : "border-transparent"}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-2.5 lg:py-0 lg:h-20">
          <Logo />
          <DesktopNav />
          <div className="order-last lg:order-none w-full lg:w-auto flex items-center justify-between lg:justify-end gap-2 sm:gap-3 pb-1 lg:pb-0 border-t border-slate-100 lg:border-0 pt-2 lg:pt-0">
            <LanguageSelector />
            <ContactMenu />
            <QuoteButton onClick={scrollToQuote} />
          </div>
          <MobileMenu />
        </div>
      </div>
    </header>
  );
};

export default Header;
